import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { settingsService, Bank } from '../../../services';

const Banks: React.FC = () => {
  const { t } = useTranslation();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    swift: '',
    active: true,
  });

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsService.getParametrizacoesBanks();
      setBanks(response.data || []);
    } catch (err: any) {
      console.error('Error fetching banks:', err);
      setError(err.message || t('settings.banks.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const getStatusChip = (active: string) => {
    return active === 'S' ? (
      <Chip size="small" color="success" label={t('common.active')} icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label={t('common.inactive')} icon={<InactiveIcon />} />
    );
  };

  const filteredBanks = banks.filter(bank => {
    const matchesSearch = searchTerm === '' ||
      bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bank.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && bank.active === 'S') ||
      (statusFilter === 'inactive' && bank.active === 'N');
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: banks.length,
    active: banks.filter(b => b.active === 'S').length,
    inactive: banks.filter(b => b.active === 'N').length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (bank?: Bank) => {
    if (bank) {
      setSelectedBank(bank);
      setFormData({
        code: bank.code,
        name: bank.name,
        swift: (bank as any).swift || '',
        active: bank.active === 'S',
      });
    } else {
      setSelectedBank(null);
      setFormData({
        code: '',
        name: '',
        swift: '',
        active: true,
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedBank(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      if (selectedBank) {
        await settingsService.updateBank(selectedBank.id, {
          name: formData.name,
          active: formData.active ? 'S' : 'N',
        });
      } else {
        await settingsService.createBank({
          code: formData.code,
          name: formData.name,
        });
      }
      handleCloseEdit();
      fetchBanks();
    } catch (err: any) {
      setError(err.message || t('settings.banks.errorSaving'));
    }
  };

  const handleDelete = async (bank: Bank) => {
    if (!window.confirm(t('settings.banks.confirmDelete', { name: bank.name }))) {
      return;
    }

    try {
      setError(null);
      await settingsService.deleteBank(bank.id);
      fetchBanks();
    } catch (err: any) {
      setError(err.message || t('settings.banks.errorDeleting'));
    }
  };

  if (loading && banks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('settings.banks.title')}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('settings.banks.newBank')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BankIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.banks.totalBanks')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.banks.activeMale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="error" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactive}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('settings.banks.inactiveMale')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('settings.banks.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('common.all')}</MenuItem>
                <MenuItem value="active">{t('settings.banks.activeMale')}</MenuItem>
                <MenuItem value="inactive">{t('settings.banks.inactiveMale')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Banks Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>
                <TableCell width={100}>{t('common.code')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell width={120}>SWIFT</TableCell>
                <TableCell width={100}>{t('common.status')}</TableCell>
                <TableCell width={100} align="center">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBanks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((bank) => (
                <TableRow key={bank.id} hover>
                  <TableCell>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                      {bank.code.substring(0, 2)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                      {bank.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {bank.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                      {(bank as any).swift || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(bank.active)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(bank)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(bank)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBanks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.paginationDisplay', { from, to, count })}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BankIcon />
          {selectedBank ? t('settings.banks.editBank') : t('settings.banks.newBank')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={t('common.code')}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
                disabled={!!selectedBank}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.banks.swiftCode')}
                value={formData.swift}
                onChange={(e) => setFormData({ ...formData, swift: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 11 }}
                helperText={t('settings.banks.swiftHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
              >
                <MenuItem value="active">{t('common.active')}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.code || !formData.name}
          >
            {selectedBank ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Banks;
